import { useCallback, useEffect, useRef, useState } from 'react';
import ReporteriaAPI from '../reporteria-api';

export const POLL_INTERVAL = 15000;
export const MAX_POLLS = 20;

export function reportDateDetails(report) {
  if (report.estado === 'pending' && report.nextExecution) {
    return { timestamp: Number(report.nextExecution), label: 'Programado para' };
  }
  if (report.estado === 'completed' || report.estado === 'failed') {
    return {
      timestamp: Number(report.fechaActualizacion || report.fechaCreacion) || null,
      label: report.estado === 'completed' ? 'Ejecutado' : 'Falló',
    };
  }
  return { timestamp: Number(report.fechaCreacion) || null, label: report.fechaCreacion ? 'Creado' : '' };
}

export function reportErrorMessage(error, fallback) {
  // request.js rejects with the Axios response itself, rather than AxiosError.
  const response = error?.response || error;
  const data = response?.data;
  const message = (typeof data === 'string' ? data : data?.message || data?.detail || data?.reason) || error?.message;
  const details = data?.details;
  const text = [message, details].filter(value => typeof value === 'string' && value.trim());
  return [...new Set(text)].join(' — ') || (response?.status ? `${fallback} (HTTP ${response.status})` : fallback);
}

export function hasExhaustedRetries(report) {
  return Number(report.maxAttempts) > 0 && Number(report.attempts) >= Number(report.maxAttempts);
}

export function shouldPollReport(report) {
  if (report.estado === 'pending' || report.estado === 'processing') return true;
  if (report.estado !== 'deferred' || hasExhaustedRetries(report)) return false;
  // Older API responses have no retry metadata: refresh them within the same limit.
  return report.retryAfter === undefined || report.retryAfter !== null;
}

async function loadHistory(pageNumber, isCurrent) {
  const response = await ReporteriaAPI.getReportLogs(pageNumber, 10);
  if (!isCurrent()) return null;
  const { data, total, limit } = response.data;
  if (!Array.isArray(data)) throw new Error('No se recibieron datos válidos del historial de reportes.');
  const detailsByConfig = new Map();
  const editsByConfig = new Map();
  const validations = {};

  const reports = await Promise.all(data.map(async log => {
    const configId = log.configuracionReporte?.id;
    const report = {
      ...log,
      nombre: log.configuracionReporte?.nombre || log.nombre || `Log ${log.tipo}`,
      description: log.configuracionReporte?.descripcion || log.descripcion || '',
    };
    const validation = { canEdit: false, canDownload: false };

    const readConfig = async () => {
      if (!configId) return;
      try {
        if (!detailsByConfig.has(configId)) detailsByConfig.set(configId, ReporteriaAPI.getReportById(configId));
        const detail = await detailsByConfig.get(configId);
        report.schedule = detail.data?.reporte?.schedule;
        report.frecuencia = report.schedule ? { tipo: report.schedule.tipo } : null;
      } catch (error) {
        report.configurationError = reportErrorMessage(error, 'No se pudo consultar la configuración.');
      }
    };

    const validateEdit = async () => {
      if (!configId) return;
      try {
        if (!editsByConfig.has(configId)) editsByConfig.set(configId, ReporteriaAPI.canEditReport(configId));
        const edit = await editsByConfig.get(configId);
        validation.canEdit = edit.data?.data?.canEdit === true;
        const reason = edit.data?.data?.reason;
        validation.editError = reason === 'deferred_pending'
          ? 'La configuración tiene reportes pendientes de generación.'
          : reason === 'currently_processing' ? 'Hay un reporte en proceso.' : reason;
      } catch (error) {
        validation.editError = reportErrorMessage(error, 'No se pudo comprobar el permiso de configuración.');
      }
    };

    const validateDownload = async () => {
      if (log.estado !== 'completed') return;
      if (!log.url) {
        validation.downloadError = 'El reporte figura completado, pero no tiene un archivo disponible.';
        return;
      }
      try {
        const download = await ReporteriaAPI.validateDownload(log.id);
        validation.canDownload = download.data?.data?.canDownload === true;
        if (!validation.canDownload) {
          validation.downloadError = download.data?.data?.reason || 'El archivo no está disponible para descarga.';
        }
      } catch (error) {
        validation.downloadError = reportErrorMessage(error, 'No se pudo comprobar la descarga del archivo.');
      }
    };

    // Each history refresh recalculates permissions, including IDs already on screen.
    await Promise.all([readConfig(), validateEdit(), validateDownload()]);
    validations[log.id] = validation;
    return report;
  }));

  return { reports, validations, totalPages: Math.max(1, Math.ceil(total / limit)) };
}

export default function useReportHistory(pageNumber) {
  const [reports, setReports] = useState([]);
  const [reportValidations, setReportValidations] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pollingPaused, setPollingPaused] = useState(false);
  const mounted = useRef(false);
  const requestId = useRef(0);
  const timer = useRef(null);
  const pollCount = useRef(0);
  const hasActiveReports = useRef(false);

  const refreshReports = useCallback(async ({ background = false, resetPolling = false } = {}) => {
    clearTimeout(timer.current);
    if (resetPolling) {
      pollCount.current = 0;
      setPollingPaused(false);
    }
    const currentRequest = ++requestId.current;
    const isCurrent = () => mounted.current && requestId.current === currentRequest;
    setIsRefreshing(true);
    if (!background) setIsLoadingReports(true);
    setErrorMessage('');
    try {
      const result = await loadHistory(pageNumber, isCurrent);
      if (!isCurrent()) return;
      setReports(result.reports);
      setReportValidations(result.validations);
      setTotalPages(result.totalPages);
      hasActiveReports.current = result.reports.some(shouldPollReport);
    } catch (error) {
      if (!isCurrent()) return;
      setErrorMessage(reportErrorMessage(error, 'No se pudo actualizar el historial de reportes.'));
    } finally {
      if (isCurrent()) {
        setIsLoadingReports(false);
        setIsRefreshing(false);
        if (hasActiveReports.current && pollCount.current < MAX_POLLS) {
          timer.current = setTimeout(() => {
            pollCount.current += 1;
            refreshReports({ background: true });
          }, POLL_INTERVAL);
        } else {
          setPollingPaused(hasActiveReports.current);
        }
      }
    }
  }, [pageNumber]);

  useEffect(() => {
    mounted.current = true;
    hasActiveReports.current = false;
    setReports([]);
    setReportValidations({});
    refreshReports({ resetPolling: true });
    return () => {
      mounted.current = false;
      requestId.current += 1;
      clearTimeout(timer.current);
    };
  }, [refreshReports]);

  return { reports, reportValidations, totalPages, isLoadingReports, isRefreshing, errorMessage, pollingPaused, refreshReports };
}
